'use strict'

// Constants
import {
    COUNTRY_TO_LANGUAGE_MAP,
    LANGUAGES_REQUIRING_LATIN_NUMERALS,
    CURRENCY_SIGNS
} from '../constants'

/**
 * Determines the appropriate currency code to use based on the conversionsRate's configuration.
 *
 * This function checks various properties of the provided conversionsRate to decide whether to use
 * a custom payment currency code or the default currency. If the conversionsRate is configured to
 * use a price converter, it returns the conversionsRate's payment currency code; otherwise, it
 * returns the provided default currency.
 *
 * @param {Object} conversionsRate - The conversionsRate object containing currency and conversion properties.
 * @param {string} currency - The default currency code to use if no custom configuration is found.
 * @returns {string} - The currency code to be used, either from the conversionsRate's configuration or the default.
 */
export const getConvertedCurrencyCode = (conversionsRate, currency) => {
    if (
        !conversionsRate ||
        !conversionsRate?.conversionRate ||
        !conversionsRate?.lcpMultiplier ||
        !conversionsRate?.paymentCurrencyCode ||
        !conversionsRate?.usePriceConverter
    ) {
        return currency
    }

    return conversionsRate.paymentCurrencyCode
}

/**
 * Retrieves international shipping conversion details from an order object.
 * @param {Object} order - The order object containing international shipping properties.
 * @param {string} [order.c_internationalShippingConversionRate] - The conversion rate for international shipping.
 * @param {string} [order.c_internationalShippingMultiplier] - The multiplier applied for international shipping.
 * @param {string} [order.c_internationalShippingCurrencyCode] - The currency code used for international shipping.
 * @param {string} [order.currency] - The default currency code of the order.
 * @param {boolean} [order.c_internationalShipping] - Indicates if the order is international.
 * @returns {Object} An object containing:
 *   @property {string} conversionRate - The conversion rate or an empty string if not available.
 *   @property {string} lcpMultiplier - The multiplier or an empty string if not available.
 *   @property {string} paymentCurrencyCode - The payment currency code for the order.
 *   @property {boolean} usePriceConverter - True if an international shipping currency code is present.
 *   @property {boolean} internationalShipping - True if the order is international.
 */
export const getOrderConversionRate = (order) => {
    const paymentCurrencyCode = order?.c_internationalShippingCurrencyCode || order?.currency
    const usePriceConverter =
        !!order?.c_internationalShippingCurrencyCode &&
        order.c_internationalShippingCurrencyCode !== order?.currency

    return {
        conversionRate: order?.c_internationalShippingConversionRate || '',
        lcpMultiplier: order?.c_internationalShippingMultiplier || '',
        paymentCurrencyCode: paymentCurrencyCode,
        usePriceConverter: usePriceConverter,
        internationalShipping: !!order?.c_internationalShipping
    }
}

/**
 * Converts a given price to a target currency using provided conversion rates and multipliers.
 *
 * Handles different conversion logic for regular products, shipping, and gift certificates.
 * If conversion rates or required properties are missing, returns the original price.
 *
 * @param {number} price - The original price to be converted.
 * @param {Object} conversionsRate - The conversion rate object containing conversion parameters.
 * @param {number} conversionsRate.conversionRate - The rate to convert the price to the target currency.
 * @param {number} conversionsRate.lcpMultiplier - The multiplier for local currency price adjustment.
 * @param {string} conversionsRate.paymentCurrencyCode - The target payment currency code.
 * @param {boolean} conversionsRate.usePriceConverter - Flag indicating if conversion should be applied.
 * @param {boolean} [isGiftCertificate=false] - Whether the price is for a gift certificate (uses only lcpMultiplier).
 * @param {boolean} [isShipping=false] - Whether the price is for shipping (uses only conversionRate).
 * @returns {number} The converted price based on the provided parameters and conversion logic.
 */
export const getConvertedPrice = (
    price,
    conversionsRate,
    isGiftCertificate = false,
    isShipping = false
) => {
    if (
        !conversionsRate ||
        !conversionsRate?.conversionRate ||
        !conversionsRate?.lcpMultiplier ||
        !conversionsRate?.paymentCurrencyCode ||
        !conversionsRate?.usePriceConverter
    ) {
        return price
    }

    // check for undefined price
    let updatedPrice = (price && !Number.isNaN(price) && price) || 0

    // for shipping conversion use only conversionRate
    if (isShipping) {
        return updatedPrice * conversionsRate?.conversionRate
    }

    // for gift certificate price conversion use only lcpMultiplier
    if (isGiftCertificate) {
        return updatedPrice * conversionsRate?.lcpMultiplier
    }

    // for product prices use conversionsRate and lcpMultiplier
    return updatedPrice * conversionsRate?.conversionRate * conversionsRate?.lcpMultiplier
}

/**
 * Retrieves the default language associated with a given country code.
 *
 * @param {string} countryCode - The ISO 3166-1 alpha-2 code representing the country.
 * @returns {string} - The default language code for the specified country.
 */
export const getDefaultLanguageForCountry = (countryCode) => {
    return COUNTRY_TO_LANGUAGE_MAP[countryCode]
}

/**
 * Formats a given price into a localized currency string based on the specified parameters.
 *
 * @param {number} price - The numeric value representing the price to be formatted.
 * @param {string} currency - The ISO 4217 currency code (e.g., 'USD', 'EUR') to format the price in.
 * @param {Object} conversionsRate - An object containing conversion settings, including a boolean `usePriceConverter` to determine if conversion is needed.
 * @param {boolean} conversionsRate.usePriceConverter - Indicates whether to use the price converter based on the shipping country.
 * @param {string} shipToCountry - The ISO 3166-1 alpha-2 country code (e.g., 'US', 'DE') representing the country to which the item is shipped.
 * @param {Object} intl - An internationalization object providing a method to format numbers.
 * @param {Function} intl.formatNumber - A function to format numbers according to locale-specific conventions.
 * @returns {string} - The formatted price string in the specified currency and locale.
 *
 * @example
 * // Assuming the conversion rate is enabled and the shipping country is 'DE':
 * formatPrice(100, 'EUR', { usePriceConverter: true }, 'DE', intl);
 * // Returns: "€100.00"
 *
 * @example
 * // Assuming the conversion rate is disabled:
 * formatPrice(100, 'USD', { usePriceConverter: false }, 'US', intl);
 * // Returns: "$100.00"
 */
export const formatPrice = (price, currency, conversionsRate, shipToCountry, intl) => {
    if (conversionsRate?.usePriceConverter && shipToCountry) {
        const defaultLanguage = getDefaultLanguageForCountry(shipToCountry)

        // Ensure prices are formatted using Latin numerals for all countries,
        // including those where Arabic is the primary language. This is done by
        // setting the language to 'en', which defaults to Latin numerals.
        // Case 1: Egypt ar-EG => en-EG to diplsay latin price (Arabic)
        // Case 2: Bangladesh bn-BD => en-BD to diplsay latin price (Bengali)
        const language = LANGUAGES_REQUIRING_LATIN_NUMERALS.includes(defaultLanguage)
            ? 'en'
            : defaultLanguage

        return new Intl.NumberFormat(`${language}-${shipToCountry}`, {
            style: 'currency',
            currency,
            currencyDisplay: 'symbol'
        }).format(price)
    } else {
        return intl
            .formatNumber(price, {
                style: 'currency',
                currency,
                currencyDisplay: 'symbol'
            })
            .replace(currency, CURRENCY_SIGNS[currency])
    }
}

/**
 * Determines if a given country code is included in the list of countries
 * where tax is included in the price.
 *
 * @param {string} countryCode - The ISO 3166-1 alpha-2 code representing the country.
 * @returns {boolean} - Returns true if the country code is in the list of countries
 *                      where tax is included in the price; otherwise, false.
 */
export const isIncludedTax = (countryCode) => {
    const pricebookCountryCodes = [
        'AD',
        'AU',
        'AT',
        'BE',
        'BG',
        'IC',
        'CN',
        'HR',
        'CY',
        'CZ',
        'DK',
        'EE',
        'FI',
        'FR',
        'DE',
        'GR',
        'GG',
        'HK',
        'HU',
        'IE',
        'IM',
        'IT',
        'JP',
        'JE',
        'LV',
        'LI',
        'LT',
        'LU',
        'MO',
        'MT',
        'MC',
        'NL',
        'PT',
        'RO',
        'SG',
        'SK',
        'SI',
        'ES',
        'SE',
        'CH',
        'GB',
        'VA'
    ]

    return pricebookCountryCodes.includes(countryCode)
}

/**
 * Determines whether additional taxes and duties should be shown for a given country.
 *
 * This function checks if the provided country code is included in a predefined list
 * of country codes where other taxes and duties are applicable. If the country code
 * is found in the list, the function returns true, indicating that other taxes and
 * duties should be shown. Otherwise, it returns false.
 *
 * @param {string} countryCode - The ISO 3166-1 alpha-2 country code to check.
 * @returns {boolean} - Returns true if other taxes and duties should be shown for the
 *                      specified country code, otherwise returns false.
 */
export const showOtherTaxAndDuties = (countryCode) => {
    const otherTaxAndDutiesCountryCode = [
        'AD',
        'AE',
        'AG',
        'AI',
        'AL',
        'AO',
        'AR',
        'AU',
        'AW',
        'AX',
        'BA',
        'BB',
        'BD',
        'BF',
        'BH',
        'BJ',
        'BL',
        'BM',
        'BN',
        'BO',
        'BR',
        'BS',
        'BT',
        'BW',
        'BZ',
        'BI',
        'CA',
        'CG',
        'CL',
        'CM',
        'CN',
        'CO',
        'CR',
        'CW',
        'DM',
        'DO',
        'EC',
        'EG',
        'ER',
        'ET',
        'FJ',
        'GA',
        'GD',
        'GF',
        'GG',
        'GH',
        'GI',
        'GM',
        'GP',
        'GQ',
        'GT',
        'GY',
        'HK',
        'HN',
        'HT',
        'IC',
        'ID',
        'IL',
        'IM',
        'IN',
        'IS',
        'JE',
        'JM',
        'JO',
        'KE',
        'KH',
        'KR',
        'KW',
        'KY',
        'LA',
        'LC',
        'LK',
        'LR',
        'LS',
        'MA',
        'ME',
        'MF',
        'MG',
        'ML',
        'MN',
        'MO',
        'MQ',
        'MS',
        'MU',
        'MV',
        'MW',
        'MX',
        'MY',
        'MZ',
        'NA',
        'NE',
        'NG',
        'NI',
        'NO',
        'NZ',
        'OM',
        'PA',
        'PE',
        'PF',
        'PG',
        'PH',
        'PK',
        'PY',
        'QA',
        'RE',
        'RS',
        'RW',
        'SA',
        'SG',
        'SL',
        'SM',
        'SN',
        'SV',
        'SZ',
        'TC',
        'TD',
        'TG',
        'TH',
        'TK',
        'TL',
        'TT',
        'TW',
        'TZ',
        'UG',
        'UY',
        'VA',
        'VC',
        'VG',
        'VN',
        'VU',
        'WF',
        'YT',
        'ZA',
        'ZM',
        'ZW'
    ]

    return otherTaxAndDutiesCountryCode.includes(countryCode)
}

/**
 * Determines if Zonos taxes should be applied based on the provided country code.
 *
 * This function checks if the given country code is included in a predefined list
 * of country codes for which Zonos taxes are applicable. It returns a boolean
 * indicating whether the country code is eligible for Zonos taxes.
 *
 * @param {string} countryCode - The ISO 3166-1 alpha-2 code representing the country
 *                               for which to check the applicability of Zonos taxes.
 * @returns {boolean} - Returns true if the country code is in the list of countries
 *                      that use Zonos taxes, otherwise returns false.
 */
export const isZonosTaxesUsed = (countryCode) => {
    const zonosTaxesCountryCode = [
        'AD',
        'AE',
        'AG',
        'AI',
        'AL',
        'AO',
        'AR',
        'AW',
        'AX',
        'BA',
        'BB',
        'BD',
        'BF',
        'BH',
        'BJ',
        'BL',
        'BM',
        'BN',
        'BO',
        'BR',
        'BT',
        'BW',
        'BZ',
        'CA',
        'CG',
        'CL',
        'CO',
        'CR',
        'CW',
        'DM',
        'DO',
        'EC',
        'EG',
        'ER',
        'ET',
        'FJ',
        'GA',
        'GD',
        'GF',
        'GH',
        'GI',
        'GM',
        'GP',
        'GQ',
        'GT',
        'GY',
        'HN',
        'HT',
        'IC',
        'ID',
        'IL',
        'IS',
        'JM',
        'JO',
        'KE',
        'KH',
        'KR',
        'KW',
        'KY',
        'LA',
        'LC',
        'LK',
        'LS',
        'ME',
        'MA',
        'MG',
        'ML',
        'MN',
        'MQ',
        'MS',
        'MU',
        'MV',
        'MW',
        'MY',
        'MZ',
        'NE',
        'NG',
        'NI',
        'NO',
        'NZ',
        'OM',
        'PA',
        'PE',
        'PG',
        'PH',
        'PK',
        'PY',
        'QA',
        'RE',
        'RS',
        'RW',
        'SA',
        'SM',
        'SV',
        'SZ',
        'TC',
        'TD',
        'TG',
        'TH',
        'TK',
        'TL',
        'TT',
        'TW',
        'TZ',
        'UG',
        'UY',
        'VA',
        'VC',
        'VG',
        'VN',
        'VU',
        'ZA',
        'ZM',
        'BS',
        'BI',
        'CM',
        'CW',
        'PF',
        'GH',
        'HT',
        'IN',
        'IM',
        'LR',
        'YT',
        'MF',
        'SN',
        'SL',
        'WF',
        'NA',
        'ZW'
    ]

    return zonosTaxesCountryCode.includes(countryCode)
}
